/**
 * BuildVersionChecker — detects stale frontend bundles after a deploy
 * and prompts the user to reload.
 *
 * Why this exists:
 *   Capgo OTA + Cloudflare CDN occasionally leave users on a stale JS
 *   bundle for hours after a deploy. The stale bundle hits new backend
 *   API contracts and crashes with "Something went wrong" on /drops
 *   (recurring P2 issue in the handoff).
 *
 * How it works:
 *   1. On mount, capture the build_id the FIRST time we successfully
 *      hit /api/version. That becomes our "session baseline".
 *   2. Re-poll every 5 minutes AND whenever the tab regains focus
 *      (visibilitychange listener).
 *   3. If the server's build_id ever differs from the captured baseline,
 *      surface a non-blocking amber banner with a one-tap "Reload" CTA
 *      that hard-reloads the page (which busts the cached JS bundle).
 *   4. User can dismiss for 30 minutes (localStorage) to avoid annoying
 *      power users who can't reload right this second.
 *
 * Deploy-time hook (optional):
 *   The backend exposes `build_id` from BUILD_ID env. If unset, it
 *   falls back to the backend process start time, so a simple
 *   `supervisorctl restart backend` will trigger the prompt for users
 *   on previously-loaded bundles — exactly the desired behaviour.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { RefreshCw, X } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_MS = 5 * 60 * 1000;          // 5 minutes
const DISMISS_KEY = "blk_version_check_dismissed_until";
const DISMISS_MS = 30 * 60 * 1000;      // 30 minutes

const BuildVersionChecker = () => {
  const [stale, setStale] = useState(false);
  const [serverBuild, setServerBuild] = useState(null);
  const baselineRef = useRef(null);

  const dismissedUntil = () => {
    try {
      const v = Number(localStorage.getItem(DISMISS_KEY) || 0);
      return v > Date.now() ? v : 0;
    } catch { return 0; }
  };

  const check = useCallback(async () => {
    if (dismissedUntil() > 0) return;
    try {
      const { data } = await axios.get(`${API}/version`, { timeout: 8000 });
      const id = data?.build_id || data?.build_time || null;
      if (!id) return;
      if (!baselineRef.current) {
        // First successful response — this is the version that loaded
        // the JS bundle currently running.
        baselineRef.current = id;
        return;
      }
      if (id !== baselineRef.current) {
        setServerBuild(id);
        setStale(true);
      }
    } catch {
      // Network error — ignore, don't spam the user with reload prompts
      // because the API blipped for 1s.
    }
  }, []);

  useEffect(() => {
    check();
    const interval = setInterval(check, POLL_MS);
    const onVisibility = () => { if (!document.hidden) check(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [check]);

  const reload = () => {
    // Bust the cache — append a query param + hard reload
    const url = new URL(window.location.href);
    url.searchParams.set("_v", Date.now().toString());
    window.location.replace(url.toString());
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS)); } catch {}
    setStale(false);
  };

  if (!stale) return null;

  return (
    <div
      className="fixed top-2 left-1/2 -translate-x-1/2 z-[60] max-w-md w-[calc(100vw-1rem)] mx-2"
      data-testid="build-version-banner"
    >
      <div className="rounded-xl bg-amber-500/95 backdrop-blur-sm border border-amber-400 shadow-2xl p-3 flex items-center gap-3">
        <RefreshCw size={18} className="text-black flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-black font-black text-xs uppercase tracking-wider">New version available</p>
          <p className="text-black/70 text-[10px]">Reload to grab the latest features and fixes.</p>
        </div>
        <button
          onClick={reload}
          className="px-3 py-1.5 rounded-lg bg-black text-amber-300 font-black text-[10px] uppercase tracking-wider hover:bg-black/80"
          data-testid="build-version-reload"
        >
          Reload
        </button>
        <button
          onClick={dismiss}
          className="text-black/60 hover:text-black p-1"
          aria-label="Dismiss"
          data-testid="build-version-dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default BuildVersionChecker;
